/**
 * Iter 150 · Shared premium primitives for the customer app (/me/*).
 * Rounded-2xl white cards, orange pill CTAs, staggered entrance, icons.
 * NO images — all decoration via Phosphor icons and gradients.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";

// ── Section wrapper (px-5 with title strip) ─────────────────────
export function MeSection({ title, action, children, testid, className = "" }) {
  return (
    <section className={`px-5 ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-3">
          {title && (
            <h2 className="font-display font-black text-[17px] tracking-tight text-[#7F1D1D]">
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function MeSectionEyebrow({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] font-bold">
      {children}
    </div>
  );
}

// ── Card (rounded-2xl white on off-white bg) ────────────────────
export function MeCard({ children, className = "", padding = "p-4", testid }) {
  return (
    <div data-testid={testid}
         className={`bg-white rounded-2xl border border-green-900/5 shadow-[0_8px_24px_rgba(15,31,12,0.04)] ${padding} ${className}`}>
      {children}
    </div>
  );
}

// ── Orange pill CTA ────────────────────────────────────────────
export function MeButton({ children, onClick, to, testid, tone = "orange",
                            size = "md", full = false, disabled, icon: Icon,
                            className = "" }) {
  const tones = {
    orange: "bg-[#F97316] hover:bg-[#EA580C] text-white shadow-[0_10px_25px_rgba(249,115,22,0.30)]",
    ghost:  "bg-white border border-[#E5E2D9] text-[#7F1D1D] hover:bg-[#F9FAF9]",
    dark:   "bg-[#7F1D1D] hover:bg-[#1B3B18] text-white",
    green:  "bg-[#DC2626] hover:bg-[#B91C1C] text-white",
  };
  const sizes = { sm: "h-9 px-4 text-[12px]", md: "h-11 px-5 text-[13px]", lg: "h-12 px-6 text-sm" };
  const cls = `${sizes[size]} rounded-full font-bold inline-flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform disabled:opacity-40 ${tones[tone]} ${full ? "w-full" : ""} ${className}`;
  const inner = (
    <>{Icon && <Icon size={14} weight="bold"/>}{children}</>
  );
  if (to) return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} className={cls}>
      {inner}
    </button>
  );
}

// ── Icon-first list row (used on Services / Health / Profile) ───
export function MeListRow({ icon: Icon, iconTone = "green", title, subtitle,
                              to, onClick, right, testid, badge }) {
  const tones = {
    green:  "bg-[#FEE2E2] text-[#DC2626]",
    orange: "bg-[#FEE7D5] text-[#F97316]",
    blue:   "bg-[#DBEAFE] text-[#1E40AF]",
    purple: "bg-[#EDE9FE] text-[#6D28D9]",
    amber:  "bg-[#FEF3C7] text-[#B45309]",
    red:    "bg-[#FEE2E2] text-[#B91C1C]",
    slate:  "bg-[#F1F5F9] text-[#334155]",
  };
  const body = (
    <>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tones[iconTone] || tones.green}`}>
        {Icon && <Icon size={20} weight="duotone"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-[14px] text-[#7F1D1D] truncate">{title}</div>
          {badge}
        </div>
        {subtitle && (
          <div className="text-[11px] text-[#4B5563] truncate mt-0.5">{subtitle}</div>
        )}
      </div>
      {right ?? <CaretRight size={12} className="text-[#9CA3AF]"/>}
    </>
  );
  const cls = "flex items-center gap-3 py-3.5 active:bg-[#F9FAF9] transition-colors";
  if (to)  return <Link to={to} data-testid={testid} className={cls}>{body}</Link>;
  if (onClick) return <button onClick={onClick} data-testid={testid} className={`${cls} w-full text-left`}>{body}</button>;
  return <div className={cls} data-testid={testid}>{body}</div>;
}

// ── Stat pill (for hero cards & summary strips) ─────────────────
export function MeStat({ label, value, tone = "light" }) {
  const tones = {
    light: "bg-white/10 text-white",
    green: "bg-[#FEE2E2] text-[#7F1D1D]",
  };
  return (
    <div className={`rounded-xl px-3 py-2 ${tones[tone]}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] opacity-70 font-bold">{label}</div>
      <div className="text-lg font-display font-bold mt-0.5 leading-none">{value}</div>
    </div>
  );
}

// ── Staggered entrance wrapper ──────────────────────────────────
export function MeStagger({ children, className = "", stagger = 40, testid }) {
  const arr = React.Children.toArray(children);
  return (
    <div className={className} data-testid={testid}>
      {arr.map((c, i) => (
        <div key={i} className="me-stagger-item"
             style={{ animationDelay: `${i * stagger}ms` }}>
          {c}
        </div>
      ))}
      <style>{`
        @keyframes meStaggerIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .me-stagger-item {
          opacity: 0;
          animation: meStaggerIn 0.32s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────
export function MeEmpty({ icon: Icon, title, subtitle, cta, testid }) {
  return (
    <MeCard testid={testid} className="text-center" padding="p-8">
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center mx-auto mb-3">
          <Icon size={26} weight="duotone"/>
        </div>
      )}
      <div className="font-display font-bold text-base text-[#7F1D1D]">{title}</div>
      {subtitle && <div className="text-[12px] text-[#4B5563] mt-1">{subtitle}</div>}
      {cta && <div className="mt-4">{cta}</div>}
    </MeCard>
  );
}

// ── Page-title strip ───────────────────────────────────────────
export function MePageTitle({ eyebrow, title, subtitle, right }) {
  return (
    <div className="px-5 pt-4 pb-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        {eyebrow && <MeSectionEyebrow>{eyebrow}</MeSectionEyebrow>}
        <h1 className="font-display font-black text-2xl leading-tight tracking-tight text-[#7F1D1D] mt-1">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-[#4B5563] mt-1">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}
